import React from "react";
import PropTypes from "prop-types";
import { flatten, uniq } from "lodash";
import {
  Stack,
  Text,
  Link,
  Image,
  Box,
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionButton,
} from "@chakra-ui/react";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";

const Authors = (props) => {
  const { authors } = props;

  if (!authors) return null;

  const uniqueAffiliations = uniq(
    flatten(
      authors.map((author) =>
        author.affiliations.map((a, i) =>
          author.departments && author.departments[i]
            ? `${author.departments[i]}, ${a}`
            : a
        )
      )
    )
  );

  const affiliationData = uniqueAffiliations.map((value, index) => ({
    index: index + 1,
    value,
  }));

  const authorData = authors.map((author, index) => ({
    affiliations:
      author.affiliations &&
      author.affiliations.map(
        (affiliation, index) =>
          uniqueAffiliations.indexOf(
            author.departments && author.departments[index]
              ? `${author.departments[index]}, ${affiliation}`
              : affiliation
          ) + 1
      ),
    hasEqualContribution: author.equalContribution || false,
    isCorresponding: author.correspondingAuthor || false,
    name: `${author.firstName} ${author.lastName}`,
    email: author.email,
    isLast: index === authors.length - 1 && index !== 0,
    orcid:
      author.orcid &&
      (author.orcid.includes("http")
        ? author.orcid
        : `https://orcid.org/${author.orcid.trim()}`),
    wbId: author.WBId && `https://wormbase.org/resources/person/${author.WBId}`,
  }));

  const correspondingAuthorData = authors
    .filter((author) => author.correspondingAuthor)
    .map((a) => ({
      email: a.email,
      firstName: a.firstName,
      lastName: a.lastName,
    }));

  const hasEqualContributionAuthor = authors.some((a) => a.equalContribution);

  if (authorData.length === 1) {
    const author = authorData[0];
    return (
      <Stack>
        <Text>
          <span>
            {author.name}

            {author.orcid && (
              <Link href={author.orcid} isExternal>
                <Image
                  alt="ORCID logo"
                  src="/static/images/orcid_16x16.png"
                  display="inline"
                />
              </Link>
            )}
          </span>
        </Text>
        <Text>
          {affiliationData.map((affiliation, index) => {
            return (
              <Text key={`affiliation-${index}`}>{affiliation.value}</Text>
            );
          })}
        </Text>
        <Text>
          Correspondence to: {author.name} {author.email}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack>
      <Text>
        {authorData.map((author, index) => {
          return (
            <span key={`author-${index}`}>
              {author.isLast && " and "}
              {author.wbId ? (
                <Link href={author.wbId} isExternal>
                  {author.name}
                </Link>
              ) : (
                author.name
              )}
              {author.affiliations && (
                <sup>{author.affiliations.sort().join(",")}</sup>
              )}
              {author.isCorresponding && <sup>&sect;</sup>}
              {author.hasEqualContribution && <sup>*</sup>}
              {author.orcid && (
                <Link href={author.orcid} isExternal>
                  <Image
                    alt="ORCID logo"
                    src="/static/images/orcid_16x16.png"
                    display="inline"
                  />
                </Link>
              )}
              {!author.isLast && ", "}
            </span>
          );
        })}
      </Text>
      {affiliationData.length > 3 ? (
        <Accordion allowToggle>
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <AccordionButton>
                  <Box as="span">
                    Affiliations{" "}
                    {isExpanded ? (
                      <MinusIcon fontSize="12px" />
                    ) : (
                      <AddIcon fontSize="12px" />
                    )}
                  </Box>
                </AccordionButton>
                <AccordionPanel>
                  {affiliationData.map((affiliation, index) => {
                    return (
                      <Text key={`affiliation-${index}`}>
                        <sup>{affiliation.index}</sup>
                        {affiliation.value}
                      </Text>
                    );
                  })}
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
        </Accordion>
      ) : (
        <>
          {affiliationData.map((affiliation, index) => {
            return (
              <Text key={`affiliation-${index}`}>
                <sup>{affiliation.index}</sup>
                {affiliation.value}
              </Text>
            );
          })}
        </>
      )}
      <Text>
        <sup>&sect;</sup>Correspondence to:
        {correspondingAuthorData.map((corresponding) => {
          return ` ${corresponding.firstName} ${corresponding.lastName} (${corresponding.email})`;
        })}
      </Text>
      {hasEqualContributionAuthor && (
        <Text>
          <sup>*</sup> Authors have equal contribution
        </Text>
      )}
    </Stack>
  );
};

Authors.propTypes = {
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      affiliations: PropTypes.arrayOf(PropTypes.string),
      equalContribution: PropTypes.bool,
      correspondingAuthor: PropTypes.bool,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
      orcid: PropTypes.string,
    })
  ),
};

Authors.defaultProps = {
  authors: null,
};

export default Authors;
